"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import confetti from "canvas-confetti";
import { SubmitButton } from "@/components/ui/button";
import { BorderBeam } from "@/components/ui/border-beam";
import { usePlanIconSrc } from "@/components/ui/plan-icon";
import type { PlanResponse } from "../hooks/use-subscription";

const PLAN_GRADIENTS: Record<string, string> = {
  solo: "rgba(84, 245, 237, 0.30)",
  growth: "rgba(255, 175, 83, 0.30)",
  business: "rgba(43, 93, 243, 0.30)",
};

const PLAN_ACCENTS: Record<string, { bg: string; text: string }> = {
  solo: { bg: "#54F5ED", text: "#06201F" },
  growth: { bg: "#FFAF53", text: "#241400" },
  business: { bg: "#2B5DF3", text: "#FFFFFF" },
};

interface SubscribeSuccessProps {
  open: boolean;
  plan: PlanResponse;
  onClose: () => void;
}

export function SubscribeSuccess({ open, plan, onClose }: SubscribeSuccessProps) {
  const [mounted, setMounted] = useState(false);
  const planIconSrc = usePlanIconSrc(plan.slug);
  useEffect(() => setMounted(true), []);

  const accent = PLAN_ACCENTS[plan.slug] ?? PLAN_ACCENTS.business;
  const grad = PLAN_GRADIENTS[plan.slug] ?? "rgba(255,255,255,0.1)";

  const headingWords = `Welcome to Arbi ${plan.name}`.split(" ");
  const LETTER_START = 0.45;
  // When the last word finishes appearing — message + button follow after.
  const revealEnd = LETTER_START + headingWords.length * 0.12 + 0.3;

  useEffect(() => {
    if (!open) return;
    const colors = [accent.bg, "#FFFFFF", "#FFD45C"];
    // One big full-screen burst once the scene settles in.
    const t = setTimeout(
      () =>
        confetti({
          particleCount: 240,
          spread: 360,
          startVelocity: 45,
          gravity: 0.9,
          ticks: 260,
          scalar: 1.1,
          origin: { x: 0.5, y: 0.45 },
          zIndex: 100001,
          disableForReducedMotion: true,
          colors,
        }),
      500,
    );
    return () => clearTimeout(t);
  }, [open, accent.bg]);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Dark, blurred backdrop — page stays faintly visible behind. */}
          <motion.div
            className="absolute inset-0 bg-black/55 backdrop-blur-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          />

          {/* Grid texture */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
              backgroundSize: "40px 40px",
              maskImage:
                "radial-gradient(120% 90% at 50% 40%, #000 30%, transparent 80%)",
              WebkitMaskImage:
                "radial-gradient(120% 90% at 50% 40%, #000 30%, transparent 80%)",
            }}
          />

          {/* Plan-colored gradient rising from the bottom */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            style={{
              background: `radial-gradient(90% 70% at 50% 120%, ${grad} 0%, transparent 70%)`,
            }}
          />

          {/* Content */}
          <div className="relative flex flex-col items-center text-center px-6 max-w-xl sm:px-8">
            <motion.img
              src={planIconSrc}
              alt=""
              className="size-24 sm:size-28"
              initial={{ scale: 0, rotate: -12, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{ type: "spring", damping: 11, stiffness: 180, delay: 0.3 }}
            />

            {/* Heading reveals one word at a time, in order. */}
            <h1 className="mt-7 flex flex-wrap items-center justify-center gap-x-[0.3em] text-3xl font-semibold text-heading tracking-[-1px] sm:mt-9 sm:text-4xl">
              {headingWords.map((word, i) => (
                <motion.span
                  key={`${word}-${i}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.4,
                    delay: LETTER_START + i * 0.12,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                >
                  {word}
                </motion.span>
              ))}
            </h1>

            <motion.p
              className="mt-4 text-base leading-6 text-contrast/55 sm:text-lg sm:leading-7"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: revealEnd, ease: [0.16, 1, 0.3, 1] }}
            >
              The world&apos;s talent is now at your fingertips, and Arbi is
              here to help you win. We can&apos;t wait to see how you grow.
            </motion.p>

            <motion.div
              className="mt-8"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: revealEnd + 0.15, ease: [0.16, 1, 0.3, 1] }}
            >
              <SubmitButton
                type="button"
                variant="primary"
                size="sm"
                onClick={onClose}
                style={{ backgroundColor: "#0a0a0b", borderColor: "rgba(255,255,255,0.14)" }}
                className="relative !h-9 !rounded-full px-6 border text-sm text-white hover:brightness-125"
              >
                Start Hiring
                <BorderBeam
                  size={36}
                  duration={5}
                  colorFrom="transparent"
                  colorTo={accent.bg}
                  borderWidth={1.5}
                />
              </SubmitButton>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
